import { InvestigationAttachment } from "../../../lusca-search-api/generated-src";

/** Mirrors the backend limits (investigation_attachments.py); the server re-checks both. */
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
export const MAX_ATTACHMENTS = 10;

export const IMAGE_TYPES = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
];

const EXTENSION_TYPES: Record<string, string> = {
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  pdf: "application/pdf",
  txt: "text/plain",
  log: "text/plain",
  csv: "text/csv",
  json: "application/json",
  zip: "application/zip",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
};

const ALLOWED_TYPES = new Set(Object.values(EXTENSION_TYPES));

/** For the hidden file input's `accept`. */
export const ACCEPT = [
  ...Object.keys(EXTENSION_TYPES).map((ext) => `.${ext}`),
  ...IMAGE_TYPES,
].join(",");

export const isImageType = (contentType?: string) =>
  !!contentType && IMAGE_TYPES.includes(contentType);

/** The type the upload will be signed for: the browser's, else guessed from the extension. */
export const contentTypeFor = (file: File): string | null => {
  const declared = (file.type || "").toLowerCase();
  if (ALLOWED_TYPES.has(declared)) return declared;
  const ext = file.name.includes(".")
    ? file.name.split(".").pop()!.toLowerCase()
    : "";
  if (!declared || declared === "application/octet-stream") {
    return EXTENSION_TYPES[ext] || null;
  }
  return null;
};

export const formatBytes = (bytes?: number): string => {
  if (bytes === undefined || bytes === null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace(/\.0$/, "")} MB`;
};

export const validateFile = (file: File): string | null => {
  if (file.size <= 0) return `${file.name} is empty`;
  if (file.size > MAX_ATTACHMENT_BYTES) {
    return `${file.name} is ${formatBytes(
      file.size
    )}; the limit is ${formatBytes(MAX_ATTACHMENT_BYTES)}`;
  }
  if (!contentTypeFor(file)) {
    return `${file.name}: only images, PDF, text, CSV, JSON, ZIP or XLSX can be attached`;
  }
  return null;
};

const pad = (n: number) => String(n).padStart(2, "0");

/**
 * Files from a paste or drop. A pasted screenshot arrives as "image.png" (or unnamed); give it a
 * timestamped name so several screenshots in one comment are distinguishable.
 */
export const filesFromTransfer = (
  transfer: DataTransfer | null,
  now: Date = new Date()
): File[] => {
  if (!transfer) return [];
  const files = Array.from(transfer.files || []);
  return files.map((file, i) => {
    if (file.name && file.name !== "image.png") return file;
    const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(
      now.getDate()
    )}-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
    const ext = (file.type.split("/")[1] || "png").replace("jpeg", "jpg");
    const suffix = files.length > 1 ? `-${i + 1}` : "";
    return new File([file], `screenshot-${stamp}${suffix}.${ext}`, {
      type: file.type || "image/png",
    });
  });
};

export const sameKeys = (
  a: Pick<InvestigationAttachment, "key">[] = [],
  b: Pick<InvestigationAttachment, "key">[] = []
) => a.length === b.length && a.every((x, i) => x.key === b[i].key);
